"use client";

import { type CSSProperties, useRef, useState } from "react";
import "./AssumptionValueField.css";

/** Min/max/step constraints and a display label for an assumption. */
export interface AssumptionSpec {
  min: number;
  max: number;
  step: number;
  label: string;
}

/**
 * Runtime value for an assumption.
 *
 * - **point** — a single fixed value.
 * - **range** — a `[lo, hi]` band with a draggable `nominal` estimate inside.
 *
 * `provenance` records where the value came from (e.g. `"ai-set"`,
 * `"user-set"`, `"baseline"`). The UI package treats it as an opaque string.
 */
export type AssumptionValue =
  | { kind: "point"; value: number; provenance: string }
  | {
      kind: "range";
      lo: number;
      hi: number;
      nominal: number;
      provenance: string;
    };

export interface AssumptionValueFieldProps {
  /** Bounds and step constraints for the assumption. */
  spec: AssumptionSpec;
  /** Current value — either a single point or a lo/hi range with a nominal inside. */
  value: AssumptionValue;
  /** Called whenever the committed value changes. */
  onCommit: (next: AssumptionValue) => void;
  /**
   * Format a raw number for display (e.g. `(n) => \`${(n * 100).toFixed(0)}%\``).
   * Used for the point input, the range's nominal input, and the bound labels.
   */
  formatValue: (n: number) => string;
  /**
   * Parse a user-typed string into a raw number.
   * Return `null` to reject the input (the field reverts to the previous value).
   */
  parseValue: (input: string) => number | null;
}

/* -------------------------------------------------------------------------- */
/* Helpers                                                                    */
/* -------------------------------------------------------------------------- */

function snapTo(v: number, step: number): number {
  return Math.round(v / step) * step;
}

function clampPct(p: number): number {
  return Math.max(0, Math.min(100, p));
}

/* -------------------------------------------------------------------------- */
/* Public component                                                           */
/* -------------------------------------------------------------------------- */

/**
 * Forks between a single-value text input (point mode) and a dual-bound
 * slider with an editable nominal estimate (range mode).
 *
 * Purely presentational — the only internal state is transient editing drafts
 * and the slider's live drag preview. All committed values flow through
 * `onCommit`.
 */
export function AssumptionValueField({
  spec,
  value,
  onCommit,
  formatValue,
  parseValue,
}: AssumptionValueFieldProps) {
  return value.kind === "range" ? (
    <RangeField
      spec={spec}
      value={value}
      onCommit={onCommit}
      formatValue={formatValue}
      parseValue={parseValue}
    />
  ) : (
    <PointField
      spec={spec}
      value={value}
      onCommit={onCommit}
      formatValue={formatValue}
      parseValue={parseValue}
    />
  );
}

/* -------------------------------------------------------------------------- */
/* PointField                                                                 */
/* -------------------------------------------------------------------------- */

function PointField({
  spec,
  value,
  onCommit,
  formatValue,
  parseValue,
}: {
  spec: AssumptionSpec;
  value: Extract<AssumptionValue, { kind: "point" }>;
  onCommit: (next: AssumptionValue) => void;
  formatValue: (n: number) => string;
  parseValue: (input: string) => number | null;
}) {
  const inputRef = useRef<HTMLInputElement | null>(null);
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState("");

  const display = formatValue(value.value);

  function startEditing() {
    setDraft(formatValue(value.value));
    setEditing(true);
    requestAnimationFrame(() => inputRef.current?.select());
  }

  function commitDraft() {
    const n = parseValue(draft);
    setEditing(false);
    setDraft("");
    if (n === null) return;
    onCommit({ kind: "point", value: n, provenance: "user-set" });
  }

  function cancelDraft() {
    setEditing(false);
    setDraft("");
  }

  return (
    <div className="as-vf-field">
      <input
        ref={inputRef}
        type="text"
        className="as-vf-input"
        value={editing ? draft : display}
        onChange={(e) => setDraft(e.target.value)}
        onFocus={startEditing}
        onBlur={commitDraft}
        onKeyDown={(e) => {
          if (e.key === "Enter") {
            e.preventDefault();
            e.currentTarget.blur();
          } else if (e.key === "Escape") {
            e.preventDefault();
            cancelDraft();
            e.currentTarget.blur();
          }
        }}
        aria-label={spec.label}
        spellCheck={false}
      />
    </div>
  );
}

/* -------------------------------------------------------------------------- */
/* RangeField                                                                 */
/* -------------------------------------------------------------------------- */

function RangeField({
  spec,
  value,
  onCommit,
  formatValue,
  parseValue,
}: {
  spec: AssumptionSpec;
  value: Extract<AssumptionValue, { kind: "range" }>;
  onCommit: (next: AssumptionValue) => void;
  formatValue: (n: number) => string;
  parseValue: (input: string) => number | null;
}) {
  // Live drag preview: while dragging we show the in-flight nominal in both
  // the slider and the nominal input, but only dispatch onCommit on release.
  const [previewNominal, setPreviewNominal] = useState<number | null>(null);
  const displayedNominal = previewNominal ?? value.nominal;

  function commit(patch: { lo: number; hi: number; nominal: number }) {
    onCommit({ kind: "range", ...patch, provenance: "user-set" });
  }

  function setLo(rawLo: number) {
    const lo = Math.max(spec.min, Math.min(rawLo, value.hi));
    const nominal = Math.max(lo, value.nominal);
    commit({ lo, hi: value.hi, nominal });
  }

  function setHi(rawHi: number) {
    const hi = Math.min(spec.max, Math.max(rawHi, value.lo));
    const nominal = Math.min(hi, value.nominal);
    commit({ lo: value.lo, hi, nominal });
  }

  function setNominal(n: number) {
    const nominal = Math.max(value.lo, Math.min(value.hi, n));
    commit({ lo: value.lo, hi: value.hi, nominal });
  }

  return (
    <div className="as-vf-range">
      <NominalInput
        spec={spec}
        value={displayedNominal}
        onCommit={setNominal}
        formatValue={formatValue}
        parseValue={parseValue}
      />
      <div className="as-vf-range-slider">
        <BoundLabel
          spec={spec}
          value={value.lo}
          side="lo"
          onCommit={setLo}
          formatValue={formatValue}
          parseValue={parseValue}
        />
        <NominalSlider
          lo={value.lo}
          hi={value.hi}
          step={spec.step}
          nominal={displayedNominal}
          onPreview={setPreviewNominal}
          onCommit={(n) => {
            setPreviewNominal(null);
            setNominal(n);
          }}
        />
        <BoundLabel
          spec={spec}
          value={value.hi}
          side="hi"
          onCommit={setHi}
          formatValue={formatValue}
          parseValue={parseValue}
        />
      </div>
    </div>
  );
}

/* -------------------------------------------------------------------------- */
/* NominalInput — editable display of the nominal estimate in range mode       */
/* -------------------------------------------------------------------------- */

function NominalInput({
  spec,
  value,
  onCommit,
  formatValue,
  parseValue,
}: {
  spec: AssumptionSpec;
  value: number;
  onCommit: (v: number) => void;
  formatValue: (n: number) => string;
  parseValue: (input: string) => number | null;
}) {
  const inputRef = useRef<HTMLInputElement | null>(null);
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState("");

  const display = formatValue(value);

  function start() {
    setDraft(formatValue(value));
    setEditing(true);
    requestAnimationFrame(() => inputRef.current?.select());
  }

  function done() {
    setEditing(false);
    const n = parseValue(draft);
    setDraft("");
    if (n === null) return;
    const snapped = snapTo(n, spec.step);
    // Skip saving if the parsed result formats identically to the original —
    // this prevents precision loss (e.g. 0.123 → "12%" → parse → 0.12).
    if (formatValue(snapped) === formatValue(value)) return;
    onCommit(snapped);
  }

  return (
    <span className="as-vf-nominal-wrap">
      <input
        ref={inputRef}
        type="text"
        className="as-vf-input as-vf-nominal-input"
        value={editing ? draft : display}
        onChange={(e) => setDraft(e.target.value)}
        onFocus={start}
        onBlur={done}
        onKeyDown={(e) => {
          if (e.key === "Enter") {
            e.preventDefault();
            e.currentTarget.blur();
          } else if (e.key === "Escape") {
            e.preventDefault();
            setEditing(false);
            setDraft("");
            e.currentTarget.blur();
          }
        }}
        aria-label={`${spec.label} nominal estimate`}
        spellCheck={false}
      />
      <span className="as-vf-nominal-tip" role="tooltip">
        Nominal value
      </span>
    </span>
  );
}

/* -------------------------------------------------------------------------- */
/* BoundLabel — click-to-edit lo/hi endpoint labels                           */
/* -------------------------------------------------------------------------- */

function BoundLabel({
  spec,
  value,
  side,
  onCommit,
  formatValue,
  parseValue,
}: {
  spec: AssumptionSpec;
  value: number;
  side: "lo" | "hi";
  onCommit: (v: number) => void;
  formatValue: (n: number) => string;
  parseValue: (input: string) => number | null;
}) {
  const [editing, setEditing] = useState(false);
  const display = formatValue(value);

  if (!editing) {
    return (
      <button
        type="button"
        className={`as-vf-bound-label side-${side}`}
        onClick={() => setEditing(true)}
        title={`Edit ${side === "lo" ? "min" : "max"}`}
        aria-label={`${side === "lo" ? "Min" : "Max"} ${display}, click to edit`}
      >
        <span>{display}</span>
        <svg
          className="as-vf-bound-edit"
          width="10"
          height="10"
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          strokeWidth="2"
          strokeLinecap="round"
          strokeLinejoin="round"
          aria-hidden="true"
        >
          <title>Edit bound</title>
          <path d="M12 20h9" />
          <path d="M16.5 3.5a2.121 2.121 0 113 3L7 19l-4 1 1-4 12.5-12.5z" />
        </svg>
      </button>
    );
  }

  return (
    <input
      type="text"
      defaultValue={formatValue(value)}
      className={`as-vf-bound-input side-${side}`}
      aria-label={`${spec.label} ${side === "lo" ? "min" : "max"}`}
      spellCheck={false}
      onBlur={(e) => {
        setEditing(false);
        const n = parseValue(e.target.value);
        if (n === null) return;
        const snapped = snapTo(n, spec.step);
        // Skip saving if the parsed result formats identically to the original —
        // this prevents precision loss (e.g. 0.123 → "12%" → parse → 0.12).
        if (formatValue(snapped) === formatValue(value)) return;
        onCommit(snapped);
      }}
      onKeyDown={(e) => {
        if (e.key === "Enter") {
          e.preventDefault();
          e.currentTarget.blur();
        } else if (e.key === "Escape") {
          e.preventDefault();
          setEditing(false);
        }
      }}
    />
  );
}

/* -------------------------------------------------------------------------- */
/* NominalSlider — single-handle slider spanning [lo, hi]                     */
/* -------------------------------------------------------------------------- */

function NominalSlider({
  lo,
  hi,
  step,
  nominal,
  onPreview,
  onCommit,
}: {
  lo: number;
  hi: number;
  step: number;
  nominal: number;
  /** Live updates while dragging — for sibling visuals like the nominal input. */
  onPreview: (n: number) => void;
  /** Final value on pointer-up; this triggers the persisted change. */
  onCommit: (n: number) => void;
}) {
  const trackRef = useRef<HTMLDivElement | null>(null);
  const draggingRef = useRef(false);

  const span = hi - lo || 1;
  const pct = clampPct(((nominal - lo) / span) * 100);

  function valueAt(clientX: number): number {
    const el = trackRef.current;
    if (!el) return lo;
    const rect = el.getBoundingClientRect();
    const p = Math.max(0, Math.min(1, (clientX - rect.left) / rect.width));
    return Math.max(lo, Math.min(hi, snapTo(lo + p * span, step)));
  }

  function onPointerDown(e: React.PointerEvent<HTMLDivElement>) {
    e.preventDefault();
    e.currentTarget.setPointerCapture(e.pointerId);
    draggingRef.current = true;
    onPreview(valueAt(e.clientX));
  }

  function onPointerMove(e: React.PointerEvent<HTMLDivElement>) {
    if (!draggingRef.current) return;
    onPreview(valueAt(e.clientX));
  }

  function onPointerUp(e: React.PointerEvent<HTMLDivElement>) {
    if (!draggingRef.current) return;
    draggingRef.current = false;
    const v = valueAt(e.clientX);
    try {
      e.currentTarget.releasePointerCapture(e.pointerId);
    } catch {
      // releasePointerCapture throws if capture was already released; ignore.
    }
    onCommit(v);
  }

  function onKeyDown(e: React.KeyboardEvent<HTMLDivElement>) {
    if (e.key === "ArrowLeft" || e.key === "ArrowDown") {
      e.preventDefault();
      onCommit(Math.max(lo, snapTo(nominal - step, step)));
    } else if (e.key === "ArrowRight" || e.key === "ArrowUp") {
      e.preventDefault();
      onCommit(Math.min(hi, snapTo(nominal + step, step)));
    }
  }

  return (
    <div
      className="as-vf-slider"
      ref={trackRef}
      onPointerDown={onPointerDown}
      onPointerMove={onPointerMove}
      onPointerUp={onPointerUp}
      onPointerCancel={onPointerUp}
    >
      <div className="as-vf-slider-track" />
      <div
        role="slider"
        tabIndex={0}
        aria-label="Nominal estimate"
        aria-valuemin={lo}
        aria-valuemax={hi}
        aria-valuenow={nominal}
        className="as-vf-slider-handle"
        style={{ left: `${pct}%` } as CSSProperties}
        onKeyDown={onKeyDown}
      />
      <span
        className="as-vf-slider-tip"
        role="tooltip"
        style={{ left: `${pct}%` } as CSSProperties}
      >
        Nominal value
      </span>
    </div>
  );
}
